<template>
  <CContainer>
    <CRow class="justify-content-center">
      <CCol md="12" class="text-start">
        <CRow class="tardy-module-header-row flex-wrap gy-2">
          <CCol class="d-flex min-w-0 col-12 col-md-6 align-items-center">
            <div class="v3-module-list-title" role="heading" aria-level="2">
              Tardy Management
            </div>
            <HelpCenterButton
              classes="ms-2"
              content-key="module_tardy_management"
            />

            <BaseButton class="ms-2 danger" rounded @click="clearModuleData">
              Clear Data
            </BaseButton>
            <p class="text-muted ms-2 mb-0" v-if="getLastResetInfo">
              Last run:
              {{
                helpers.convertUTCToLocalTime(
                  getLastResetInfo.run_at,
                  null,
                  "MM/DD hh:mm A"
                )
              }}
            </p>
          </CCol>
          <CCol
            class="d-flex flex-row-reverse align-items-center col-12 col-md-6"
          >
            <CustomSwitch
              type="radio"
              aria-label="Tardy Management, toggle module on or off"
              :model-value="state.isActive"
              @change="toggleTardy(state.isActive)"
            ></CustomSwitch>
          </CCol>
        </CRow>
        <div class="v3-module-list-box" v-if="state.isActive">
          <div>
            <p class="fw-bold mb-2" role="heading" aria-level="3">
              Tardy input method options:
            </p>

            <div class="ms-3 mb-4">
              <BaseButton
                class="mb-2"
                data-testid="tardy-csv-setup-button"
                @click="goToTardyCsvUploadPage"
                rounded
                >CSV Setup</BaseButton
              >
              <p class="mb-0 small text-muted">
                Files can be manually uploaded or automated via SFTP. Click "CSV
                Setup" for details.
              </p>
            </div>

            <p class="mb-3">Adult initiated (Teacher Pass)</p>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                :model-value="state.form.allowAdmins"
                test-id="tardy-allow-admins"
                label="Allow admin only - Only admin level users can mark passes as LTS or LTC."
                @change="selectAllowAdmins"
              ></CustomCheck>
            </div>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                :model-value="state.form.allowTeacherStaff"
                test-id="tardy-allow-teacher-staff"
                label="Allow all adult users - All admin, teachers, and staff members can record tardy passes."
                @change="allowTeacherStaff"
              ></CustomCheck>
            </div>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                :model-value="state.form.allowSelectedUsersOnly"
                test-id="tardy-allow-selected-users"
                label="Only allow specific teachers/staff - Designate which adults can record tardy instances. Click on add adults to create or manage this list."
                @change="allowSelectedUsersOnly"
              ></CustomCheck>
            </div>
            <BaseButton
              v-if="state.form.allowSelectedUsersOnly"
              class="ms-3 mb-4"
              data-testid="tardy-add-adults-button"
              @click="goToSelectAdultsPage"
              rounded
              >Add Adults</BaseButton
            >

            <p class="mb-3">Student initiated (Kiosk and Student Late Pass)</p>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                v-model="state.form.allowStudentsLTS"
                test-id="tardy-allow-students-lts"
                label="Mark late to school (LTS)"
                @change="allowStudentsLTS"
              ></CustomCheck>
            </div>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                v-model="state.form.allowStudentsLTC"
                test-id="tardy-allow-students-ltc"
                label="Mark late to class (LTC)"
                @change="allowStudentsLTC"
              ></CustomCheck>
            </div>

            <p class="fw-bold mb-2" role="heading" aria-level="3">
              Student view options:
            </p>

            <div class="v3-module-list-checkbox d-flex ms-3 mb-3">
              <CustomCheck
                v-model="state.form.allowStudentsSeeLevel"
                test-id="tardy-allow-students-see-ladder"
                label="Allow students to see ladder"
                @change="submit"
              ></CustomCheck>
            </div>

            <div
              class="d-flex tardy-module-student-options flex-wrap align-items-start mt-3"
            >
              <p
                class="col d-flex min-w-0 col-12 col-md-6 text-start align-items-center mb-0"
              >
                Count SLP as a pass against a student pass limit
              </p>
              <div
                class="col d-flex flex-row-reverse align-items-center col-12 col-md-6"
              >
                <CustomSwitch
                  aria-label="Count SLP as a pass against a student pass limit"
                  :model-value="state.slpCount"
                  @change="toggleSLPCount"
                ></CustomSwitch>
              </div>
            </div>
          </div>
        </div>
        <CAlert
          v-if="state.reqResponse.message"
          dismissible
          class="mt-4"
          :color="state.reqResponse.type"
          :show="!!state.reqResponse.message"
          role="status"
          aria-live="polite"
          @close="resetResponseMessages"
        >
          {{ state.reqResponse.message }}
        </CAlert>
      </CCol>
    </CRow>
  </CContainer>
</template>
<script>
import HelpCenterButton from "../../HelpCenterButton.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import CustomCheck from "@/v3components/shared/Form/CustomCheck.vue"
import CustomSwitch from "@/v3components/shared/Form/CustomSwitch.vue"
import { useStore } from "vuex"
import { onMounted, computed, reactive, ref, inject } from "vue"
import { useRouter } from "vue-router"
import helpers from "@/helpers"
import passConstants from "@/constants/passConstants"

export default {
  name: "TardyManagementModuleForm",
  components: {
    HelpCenterButton,
    BaseButton,
    CustomCheck,
    CustomSwitch
  },
  setup() {
    const store = useStore()
    const actionDialog = inject("actionDialog")
    const router = useRouter()

    const state = reactive({
      form: {
        allowAdmins: true,
        allowTeacherStaff: false,
        allowSelectedUsersOnly: false,
        allowStudentsLTC: false,
        allowStudentsLTS: false,
        // Ladder defaults on per PASS-8080, matching the backend module
        // default; setForm reflects the stored value once options load.
        allowStudentsSeeLevel: true,
        usageCSV: false,
        fileUploadMode: passConstants.DEFAULT_FILE_UPLOAD_MODE_FOR_TARDY
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      isActive: false,
      slpCount: false
    })

    const isLoading = ref(false)

    const schoolDomains = computed(() => store.getters["schools/schoolDomains"])
    const currentUser = computed(() => store.getters["authentication/user"])

    const options = computed(() => {
      const tardyModule = store.state.schools.activeSchoolModules.find(
        (module) => module.name === "Tardy Management"
      )
      return tardyModule ? JSON.parse(tardyModule.pivot.option_json) : false
    })

    const active = computed(() => {
      const tardyModule = store.state.schools.activeSchoolModules.find(
        (module) => module.name === "Tardy Management"
      )
      return tardyModule ? Boolean(tardyModule.pivot.status) : false
    })
    onMounted(() => {
      if (options.value) {
        setForm()
      }
      state.isActive = active.value
    })

    const setForm = () => {
      // option_json is nullable, so a module that has never been configured
      // parses to null. Keep the current form state rather than reading it.
      if (!options.value) return

      state.form.allowTeacherStaff = Boolean(
        options.value["allow-teacher-staff"]
      )
      state.form.allowSelectedUsersOnly = Boolean(
        options.value["allow-selected-users-only"]
      )
      state.form.allowStudentsLTS = Boolean(
        options.value["allow-students-create"]?.["lts"]
      )
      state.form.allowStudentsLTC = Boolean(
        options.value["allow-students-create"]?.["ltc"]
      )
      // Mirrors the backend module default of true. The key is always present
      // in option_json, since OptionJsonRule rejects saves that omit it, so the
      // fallback only guards a malformed row.
      state.form.allowStudentsSeeLevel = Boolean(
        options.value["allow-students-see-level"] ?? true
      )
      state.form.usageCSV = Boolean(options.value.usage?.["via-csv"])
      // Falls back to within-ehp for schools not yet covered by the
      // allow-admins backfill migration, matching the backend policy.
      state.form.allowAdmins = Boolean(
        options.value["allow-admins"] ?? options.value.usage?.["within-ehp"]
      )
      state.form.fileUploadMode =
        options?.value?.["file-upload-mode"] ||
        passConstants.DEFAULT_FILE_UPLOAD_MODE_FOR_TARDY
      state.slpCount = Boolean(options.value?.["count-slp-against-pass-limit"])
    }
    const getLastResetInfo = computed(() => {
      const lastResetInfo = options.value?.reset_history ?? []
      return lastResetInfo.length > 0
        ? lastResetInfo[lastResetInfo.length - 1]
        : null
    })

    // Per PASS-7087, "Allow All Adult Users" and "Only allow specific
    // teachers/staff" are mutually exclusive, since they describe conflicting
    // sets of adults. "Allow Admin only" is independent and pairs with either
    // of them. Leaving all three off disables adult-initiated tardies.
    const selectAllowAdmins = (value) => {
      state.form.allowAdmins = value
      submit()
    }

    const allowTeacherStaff = (value) => {
      state.form.allowTeacherStaff = value
      if (value) {
        state.form.allowSelectedUsersOnly = false
      }
      submit()
    }

    const allowStudentsLTC = (value) => {
      state.form.allowStudentsLTC = value
      submit()
    }

    const allowStudentsLTS = (value) => {
      state.form.allowStudentsLTS = value
      submit()
    }

    const allowSelectedUsersOnly = (value) => {
      state.form.allowSelectedUsersOnly = value
      if (value) {
        state.form.allowTeacherStaff = false
      }
      submit()
    }

    const submit = () => {
      setTimeout(() => {
        resetResponseMessages()
        const inputsData = JSON.stringify({
          "allow-admins": Boolean(state.form.allowAdmins),
          "allow-teacher-staff": Boolean(state.form.allowTeacherStaff),
          "allow-selected-users-only": Boolean(
            state.form.allowSelectedUsersOnly
          ),
          "allow-students-create": {
            ltc: Boolean(state.form.allowStudentsLTC),
            lts: Boolean(state.form.allowStudentsLTS)
          },
          "allow-students-see-level": Boolean(state.form.allowStudentsSeeLevel),
          "file-upload-mode": state.form.fileUploadMode,
          "count-slp-against-pass-limit": Boolean(state.slpCount),
          usage: {
            "via-csv": Boolean(state.form.usageCSV),
            // Master switch for recording tardies on passes. The backend
            // requires it for student kiosk passes as well as adult ones, so
            // any enabled option has to set it.
            "within-ehp":
              state.form.allowAdmins ||
              state.form.allowTeacherStaff ||
              state.form.allowSelectedUsersOnly ||
              state.form.allowStudentsLTS ||
              state.form.allowStudentsLTC
          }
        })
        isLoading.value = true
        store
          .dispatch("modules/updateTardyModule", { option_json: inputsData })
          .then(() => {
            setSuccessResponse("Module updated.")
            store
              .dispatch("schools/getActiveModules", {
                clearCacheEntry: true
              })
              .then(() => {
                setForm()
              })
            isLoading.value = false
          })
          .catch((err) => {
            const response = err.response?.data || {}
            setErrorResponse(response.message, response.errors)
            isLoading.value = false
            store
              .dispatch("schools/getActiveModules", {
                clearCacheEntry: true
              })
              .then(() => {
                setForm()
              })
          })
      }, 100)
    }

    const toggleTardy = (status) => {
      store
        .dispatch("modules/toggleModule", {
          id: 5,
          moduleStatus: { status: status ? 0 : 1 }
        })
        .then(() => {
          state.isActive = !state.isActive
          store
            .dispatch("schools/getActiveModules", {
              clearCacheEntry: true
            })
            .then(() => {
              getExceededIncidents()
            })
        })
    }

    const toggleSLPCount = (value) => {
      state.slpCount = Boolean(value)
      submit()
    }

    const goToTardyCsvUploadPage = () => {
      router.push("/tardy-management/tardy-csv-upload")
    }

    const getExceededIncidents = () => {
      state.isActive
        ? store.dispatch("exceededIncidents/getExceededIncidents")
        : store.commit("exceededIncidents/SET_EXCEEDED_INCIDENTS", false)
    }

    const goToSelectAdultsPage = () => {
      router.push("/tardy-management/adults")
    }

    const clearModuleData = () => {
      actionDialog.open(clearModule, {
        props: {
          danger: true,
          title: "Clear Tardy Management Module data?",
          question:
            "This action will clear all the data for the Tardy Management Module. \n\n[Tardy Uploads, Tardy Detentions and Calendar, Consequences Ladder, Tardy Ladder Reset Date]"
        }
      })
    }

    const clearModule = () => {
      store.dispatch("modules/clearTardyModuleData").then(() => {
        setSuccessResponse("Tardy Management Module data cleared.")
        state.isActive = false
        store
          .dispatch("schools/getActiveModules", {
            clearCacheEntry: true
          })
          .then(() => {
            setForm()
          })
      })
    }

    const resetResponseMessages = () => {
      state.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    }

    const setSuccessResponse = (message) => {
      state.reqResponse = {
        type: "success",
        message: message ? message : "Success!",
        errors: {}
      }
    }

    const setErrorResponse = (message, errors) => {
      state.reqResponse = {
        type: "danger",
        message: message ? message : "Something went wrong!",
        errors: errors ? errors : {}
      }
    }
    return {
      state,
      schoolDomains,
      currentUser,
      setForm,
      selectAllowAdmins,
      goToTardyCsvUploadPage,
      allowTeacherStaff,
      allowStudentsLTC,
      allowStudentsLTS,
      allowSelectedUsersOnly,
      submit,
      toggleTardy,
      goToSelectAdultsPage,
      toggleSLPCount,
      resetResponseMessages,
      clearModuleData,
      getLastResetInfo,
      helpers
    }
  }
}
</script>
